import { useEffect, useRef, useState } from 'react';

// Once a tracker step reaches COMPLETE, keep polling for this long so rows that
// land moments after COMPLETE aren't missed. Armed at most once per page load, reset on org switch.
export const GRACE_WINDOW_MS = 60000;

/**
 * Arms a one-shot grace-period timer once per `resetKey` the first time `isComplete` turns
 * true, and reports `windowExpired` once `windowMs` elapses. A later re-render that still reads
 * `isComplete` for the SAME `resetKey` is ignored — the window has already run. Switching
 * `resetKey` (e.g. an org switch) resets the window so a fresh one arms on the next `isComplete`.
 * `enabled=false` never arms (e.g. a non-owner instance that must not start its own countdown).
 *
 * `onArm` fires once, at the moment the window arms — callers use it to clear state that belongs
 * to the phase the window is superseding (e.g. a "pending budget exhausted" flag).
 */
export function useGraceWindow({
    isComplete,
    resetKey,
    windowMs,
    enabled = true,
    onArm,
}: {
    isComplete: boolean;
    resetKey: string;
    windowMs: number;
    enabled?: boolean;
    onArm?: () => void;
}): { windowExpired: boolean } {
    const armedRef = useRef(false);
    const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const [windowExpired, setWindowExpired] = useState(false);

    // Resets on a genuine key change — a stale armed/expired window from the previous key must
    // not leak into the next one.
    useEffect(() => {
        armedRef.current = false;
        setWindowExpired(false);
        if (timerRef.current) {
            clearTimeout(timerRef.current);
            timerRef.current = null;
        }
    }, [resetKey]);

    // Arms the window exactly once per (resetKey, isComplete) pair — a later re-render that
    // still reads isComplete for the SAME key must not restart the countdown. armedRef already
    // guards re-entry, so a changed `onArm` identity on a later render just re-checks and no-ops.
    useEffect(() => {
        if (!enabled || !isComplete || armedRef.current) return;
        armedRef.current = true;
        onArm?.();
        timerRef.current = setTimeout(() => setWindowExpired(true), windowMs);
    }, [enabled, isComplete, resetKey, windowMs, onArm]);

    useEffect(
        () => () => {
            if (timerRef.current) clearTimeout(timerRef.current);
        },
        [],
    );

    return { windowExpired };
}
